import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  Close,
  DarkModeOutlined,
  KeyOutlined,
  LightModeOutlined,
  Logout,
  Menu as MenuIcon,
  NotificationsNoneOutlined,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useColorMode } from "../contexts/ColorModeContext";
import { getRoleDisplayName } from "../utils/permissions";
import {
  getNavigation,
  getPageContext,
  isNavigationActive,
  navigationSections,
} from "../utils/navigation";
import NavigationIcon from "./NavigationIcon";
import GlobalSearch from "./GlobalSearch";
import AppBreadcrumbs from "./AppBreadcrumbs";
import NotificationPanel from "./NotificationPanel";
import { useNotifications } from "../hooks/useNotifications";

const SIDEBAR_KEY = "laser-control:sidebar";
const initialCollapsed = () => {
  try {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    if (stored) return stored === "collapsed";
    return (
      window.matchMedia?.("(min-width: 900px) and (max-width: 1199px)")
        .matches || false
    );
  } catch {
    return false;
  }
};
export default function Layout({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({ support: false, admin: false });
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [notificationAnchor, setNotificationAnchor] =
    useState<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { state, logout, unreadNotificationsCount } = useApp();
  const { mode, toggleColorMode } = useColorMode();
  const compact = collapsed && !mobile;
  const width = mobile ? 280 : compact ? 76 : 248;
  const user = state.auth.user;
  const items = getNavigation(user?.role).filter((item) => item.menu !== false);
  const context = getPageContext(location.pathname, user?.role);
  const unreadMessages = state.messages.filter((m) => !m.read).length;
  useNotifications();
  useEffect(() => {
    if (context?.section === "support" || context?.section === "admin")
      setExpandedSections((value) => ({ ...value, [context.section]: true }));
  }, [context?.section]);
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? "collapsed" : "expanded");
    } catch {
      /* Preferences are optional. */
    }
  }, [collapsed]);
  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);
  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#ECECEE",
        bgcolor: "#191B1F",
      }}
    >
      <Box
        sx={{
          minHeight: 72,
          px: compact ? 2.5 : 2.2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <ButtonBase
          component={RouterLink}
          to="/dashboard"
          aria-label="Laser Control · Panoramica"
          sx={{
            borderRadius: 1.5,
            display: "flex",
            textAlign: "left",
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              bgcolor: "#FA8C21",
              color: "#24180C",
              borderRadius: "9px",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: "-.06em",
            }}
          >
            CM
          </Box>
          {!compact && (
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 750,
                  lineHeight: 1.3,
                  color: "#F4F4F5",
                }}
              >
                Laser Control
              </Typography>
              <Typography
                sx={{ fontSize: 10, letterSpacing: ".08em", color: "#A2A5AD" }}
              >
                INDUSTRIAL PLATFORM
              </Typography>
            </Box>
          )}
        </ButtonBase>
        {mobile && (
          <IconButton
            aria-label="Chiudi menu"
            onClick={() => setMobileOpen(false)}
            sx={{ color: "inherit" }}
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1.5,
          scrollbarWidth: "thin",
          scrollbarColor: "#45484F transparent",
        }}
      >
        {navigationSections.map((section) => {
          const group = items.filter((item) => item.section === section.id);
          if (!group.length) return null;
          const foldable = section.id === "support" || section.id === "admin";
          return (
            <Box key={section.id} sx={{ mb: compact ? 0.8 : 1.5 }}>
              {compact ? (
                <Divider
                  sx={{ mx: 2.5, my: 1, borderColor: "rgba(255,255,255,.08)" }}
                />
              ) : foldable ? (
                <ButtonBase
                  onClick={() =>
                    setExpandedSections((value) => ({
                      ...value,
                      [section.id]: !value[section.id],
                    }))
                  }
                  aria-expanded={!!expandedSections[section.id]}
                  aria-controls={`nav-group-${section.id}`}
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                    px: 2.5,
                    py: 0.8,
                    color: "#92959E",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".1em",
                    }}
                  >
                    {section.label}
                  </Typography>
                  <ExpandMore
                    sx={{
                      fontSize: 15,
                      transform: expandedSections[section.id]
                        ? "rotate(180deg)"
                        : "none",
                    }}
                  />
                </ButtonBase>
              ) : (
                <Typography
                  sx={{
                    px: 2.5,
                    py: 0.8,
                    color: "#92959E",
                    fontSize: 9.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".1em",
                  }}
                >
                  {section.label}
                </Typography>
              )}
              <Collapse
                in={compact || !foldable || !!expandedSections[section.id]}
                id={`nav-group-${section.id}`}
              >
                <List disablePadding aria-label={section.label}>
                  {group.map((item) => {
                    const active = isNavigationActive(item, location.pathname);
                    const icon = (
                      <Badge
                        color="error"
                        variant="dot"
                        invisible={item.path !== "/messages" || !unreadMessages}
                      >
                        <NavigationIcon name={item.icon} />
                      </Badge>
                    );
                    return (
                      <Tooltip
                        key={item.path}
                        title={compact ? item.label : ""}
                        placement="right"
                        arrow
                      >
                        <ListItemButton
                          component={RouterLink}
                          to={item.path}
                          selected={active}
                          aria-current={active ? "page" : undefined}
                          aria-label={item.label}
                          onClick={() => setMobileOpen(false)}
                          sx={{
                            mx: compact ? 1.5 : 1.25,
                            my: 0.2,
                            px: compact ? 1.7 : 1.2,
                            minHeight: 38,
                            borderRadius: 1.5,
                            color: active ? "#fff" : "#B8BBC2",
                            position: "relative",
                            gap: 1.1,
                            "&.Mui-selected": {
                              bgcolor: "rgba(250,140,33,.14)",
                              "&:hover": { bgcolor: "rgba(250,140,33,.21)" },
                            },
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,.055)",
                              color: "#fff",
                            },
                            "&::before": active
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  left: -5,
                                  top: 10,
                                  bottom: 10,
                                  width: 3,
                                  bgcolor: "#FA8C21",
                                  borderRadius: 1,
                                }
                              : undefined,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 20,
                              color: active ? "#FFAA56" : "inherit",
                            }}
                          >
                            {icon}
                          </ListItemIcon>
                          {!compact && (
                            <ListItemText
                              primary={item.label}
                              sx={{ m: 0 }}
                              primaryTypographyProps={{
                                fontSize: 12.5,
                                fontWeight: active ? 700 : 500,
                                noWrap: true,
                              }}
                            />
                          )}
                          {!compact &&
                            item.path === "/messages" &&
                            unreadMessages > 0 && (
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 10,
                                  bgcolor: "rgba(255,255,255,.12)",
                                  px: 0.7,
                                  borderRadius: 1,
                                }}
                              >
                                {unreadMessages}
                              </Box>
                            )}
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,.08)", p: 1.5 }}>
        {!mobile && (
          <Tooltip title={compact ? "Espandi menu" : ""} placement="right">
            <ButtonBase
              onClick={() => setCollapsed((v) => !v)}
              aria-label={
                compact ? "Espandi menu laterale" : "Riduci menu laterale"
              }
              aria-expanded={!compact}
              sx={{
                width: "100%",
                minHeight: 38,
                borderRadius: 1.5,
                color: "#B8BBC2",
                justifyContent: compact ? "center" : "space-between",
                px: 1,
                "&:hover": { bgcolor: "rgba(255,255,255,.06)" },
              }}
            >
              {!compact && (
                <Typography variant="caption">Riduci menu</Typography>
              )}
              {compact ? (
                <ChevronRight fontSize="small" />
              ) : (
                <ChevronLeft fontSize="small" />
              )}
            </ButtonBase>
          </Tooltip>
        )}
        {!compact && (
          <Typography
            sx={{ color: "#92959E", fontSize: 10, px: 1, mt: mobile ? 0 : 0.8 }}
          >
            CM · Software e macchine
          </Typography>
        )}
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        component="a"
        href="#app-main"
        sx={{
          position: "fixed",
          top: -60,
          left: 16,
          zIndex: 2000,
          p: 1.5,
          borderRadius: 1,
          color: "primary.contrastText",
          bgcolor: "primary.main",
          "&:focus": { top: 12 },
        }}
      >
        Vai al contenuto
      </Box>
      <Box
        component="nav"
        aria-label="Navigazione principale"
        sx={{ width: { md: width }, flexShrink: 0 }}
      >
        <Drawer
          variant={mobile ? "temporary" : "permanent"}
          open={mobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width,
              boxSizing: "border-box",
              border: 0,
              boxShadow: "none",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${width}px)` },
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            top: 0,
            color: "text.primary",
            zIndex: theme.zIndex.drawer - 1,
            backdropFilter: "blur(16px)",
          }}
        >
          <Toolbar
            sx={{
              minHeight: "64px !important",
              px: { xs: 1.5, md: 3 },
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            {mobile && (
              <IconButton
                aria-label="Apri menu principale"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(true)}
                edge="start"
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <AppBreadcrumbs />
            </Box>
            <GlobalSearch />
            <Tooltip title={mode === "dark" ? "Tema chiaro" : "Tema scuro"}>
              <IconButton
                onClick={toggleColorMode}
                aria-label={
                  mode === "dark"
                    ? "Passa al tema chiaro"
                    : "Passa al tema scuro"
                }
              >
                {mode === "dark" ? (
                  <LightModeOutlined sx={{ fontSize: 20 }} />
                ) : (
                  <DarkModeOutlined sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Notifiche">
              <IconButton
                aria-label={
                  unreadNotificationsCount
                    ? `Notifiche: ${unreadNotificationsCount} non lette`
                    : "Notifiche"
                }
                aria-expanded={!!notificationAnchor}
                onClick={(e) => setNotificationAnchor(e.currentTarget)}
              >
                <Badge badgeContent={unreadNotificationsCount} color="error">
                  <NotificationsNoneOutlined sx={{ fontSize: 21 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ my: 2, mx: 0.5, display: { xs: "none", sm: "block" } }}
            />
            <Tooltip title="Il tuo account">
              <ButtonBase
                aria-label="Apri menu account"
                aria-controls={accountAnchor ? "account-menu" : undefined}
                aria-haspopup="menu"
                aria-expanded={!!accountAnchor}
                onClick={(e) => setAccountAnchor(e.currentTarget)}
                sx={{ borderRadius: 2, p: 0.6, gap: 1 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {user?.name
                    ?.split(" ")
                    .slice(0, 2)
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase() || "U"}
                </Avatar>
                <Box
                  sx={{
                    display: { xs: "none", xl: "block" },
                    textAlign: "left",
                    maxWidth: 130,
                  }}
                >
                  <Typography variant="body2" noWrap fontWeight={700}>
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user ? getRoleDisplayName(user.role) : "Account"}
                  </Typography>
                </Box>
              </ButtonBase>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <NotificationPanel
          anchorEl={notificationAnchor}
          onClose={() => setNotificationAnchor(null)}
        />
        <Menu
          id="account-menu"
          anchorEl={accountAnchor}
          open={!!accountAnchor}
          onClose={() => setAccountAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { minWidth: 250, mt: 1 } } }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography fontWeight={700}>{user?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" color="primary.main">
              {user ? getRoleDisplayName(user.role) : ""}
            </Typography>
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setAccountAnchor(null);
              navigate("/security");
            }}
          >
            <ListItemIcon>
              <KeyOutlined fontSize="small" />
            </ListItemIcon>
            Sicurezza account
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAccountAnchor(null);
              void logout();
            }}
          >
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Esci dall’account
          </MenuItem>
        </Menu>
        <Box
          component="main"
          id="app-main"
          ref={mainRef}
          tabIndex={-1}
          aria-label={context?.label || "Contenuto principale"}
          sx={{
            px: { xs: 2, sm: 3, xl: 4 },
            py: { xs: 3, md: 3.5 },
            outline: 0,
          }}
        >
          <Box
            sx={{
              maxWidth: 1520,
              mx: "auto",
              animation: "page-enter 180ms ease-out",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
            key={location.pathname}
          >
            {children}
          </Box>
        </Box>
        <Box
          component="footer"
          sx={{
            maxWidth: 1600,
            mx: "auto",
            px: { xs: 2, md: 4 },
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Laser Control · CM
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Software · Produzione · Assistenza
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
